import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trackEvent } from '../utils/analytics';
import { clarityIdentify } from '../utils/clarity';
import { setSentryUser, clearSentryUser } from '../utils/sentry';
import { phIdentify, phReset } from '../utils/posthog';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore from localStorage
    const storedUser = localStorage.getItem('db_user');
    const storedAdmin = localStorage.getItem('db_admin');
    if (storedUser) { try { const u = JSON.parse(storedUser); setUser(u); setSentryUser(u); phIdentify(u); } catch { /* corrupt */ } }
    if (storedAdmin) { try { const a = JSON.parse(storedAdmin); setAdmin(a); setSentryUser(a); } catch { /* corrupt */ } }
    setLoading(false);
  }, []);

  const loginUser = (userData, token) => {
    const data = { ...userData, token };
    setUser(data);
    localStorage.setItem('db_user', JSON.stringify(data));
    localStorage.setItem('db_token', token);
    setSentryUser(data);
    // Analytics — user-only (admin login is intentionally untracked)
    try {
      if (data.id || data.email) clarityIdentify(data.id || data.email, data.email);
      phIdentify(data);
      trackEvent('User_Login', { method: 'otp' });
    } catch {
      // ignore
    }
  };

  const loginAdmin = (adminData, token) => {
    const data = { ...adminData, token };
    setAdmin(data);
    localStorage.setItem('db_admin', JSON.stringify(data));
    localStorage.setItem('db_admin_token', token);
    setSentryUser(data);
  };

  const logoutUser = () => {
    setUser(null);
    localStorage.removeItem('db_user');
    localStorage.removeItem('db_token');
    clearSentryUser();
    phReset();
  };

  const logoutAdmin = useCallback(() => {
    setAdmin(null);
    localStorage.removeItem('db_admin');
    localStorage.removeItem('db_admin_token');
    clearSentryUser();
  }, []);

  // Auth-aware fetch for END-USER authenticated destinations (e.g. dashboard):
  // auto-logout + redirect to /login on session expiry. Mirrors adminFetch.
  // NOT used for anonymous/mid-flow calls (questionnaire, checkout) to avoid
  // any new mid-flow redirects.
  const userFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, options);
    if (res.status === 401 || res.status === 403) {
      logoutUser();
      window.location.href = '/login';
      throw new Error('Session expired');
    }
    return res;
  }, []);

  // Auth-aware fetch: auto-logout admin on 401/403
  const adminFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, options);
    if (res.status === 401 || res.status === 403) {
      logoutAdmin();
      window.location.href = '/admin/login';
      throw new Error('Session expired');
    }
    return res;
  }, [logoutAdmin]);

  return (
    <AuthContext.Provider value={{ user, admin, loading, loginUser, loginAdmin, logoutUser, logoutAdmin, adminFetch, userFetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
